import { createApp } from 'vue';
import App from './App.vue';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap";
import axios from "axios";
import router from "../router/router.js";
import phoneDirective from './directives/phone.js';
import PrimeVue from 'primevue/config';
// import 'primevue/resources/themes/aura-light-green/theme.css'
import 'primeicons/primeicons.css'
import Aura from '@primeuix/themes/aura';
import '@/assets/scss/style.min.css'
import '@/assets/css/player.css'
import store from '../src/store/index.js';
import 'vue-plyr/dist/vue-plyr.css'
import {
    isReservationPath,
    removeAuthorizationHeader,
    resetAuthenticationForGuest,
} from '@/services/guestSession';
import OfflineManager from '@/services/OfflineManager';
import AppUpdateManager from '@/services/AppUpdateManager';
import deviceInfo from '@/services/deviceInfo';
import AuthMonitor from '@/services/AuthMonitor';
import './registerServiceWorker';


axios.defaults.baseURL = localStorage.getItem("api_base_url") || "https://api.tarekmath.com/api";
// axios.defaults.baseURL = "http://192.168.1.26:8000/api";

const initialReservationGuestRoute = isReservationPath(window.location.pathname);
const initialHomepagePreview = window.location.pathname === '/__preview/home';
if (initialReservationGuestRoute) {
    // Run before the app mounts so even App-level startup requests are guest
    // requests on a direct /reservation page load.
    resetAuthenticationForGuest(store, axios);
} else if (!initialHomepagePreview) {
    const token = localStorage.getItem("token");
    if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
}

axios.interceptors.request.use((config) => {
    // The dedicated preview only consumes public homepage feeds. A student
    // session stored on the website must not turn these into private requests.
    if (window.location.pathname === '/__preview/home') {
        removeAuthorizationHeader(config.headers);
        return config;
    }
    const onReservationGuestRoute = router.currentRoute.value.matched
        .some(record => record.meta.forceGuest)
        || isReservationPath(window.location.pathname);

    // An endpoint can opt in only by setting both requiresAuth: true and its own
    // explicit Authorization header. Normal reservation requests are always guest.
    if (onReservationGuestRoute && config.requiresAuth !== true) {
        removeAuthorizationHeader(config.headers);
    }

    return config;
});

// Content protection (Features 6/7): when the API rejects a request because the
// student has no active subscription, route to the "Subscription Required" page
// and preserve the current location as the return URL.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const data = error?.response?.data;
        if (error?.response?.status === 403 && data?.code === 'subscription_required') {
            const current = router.currentRoute.value;
            if (current.path !== '/subscription-required') {
                const sub = data.subscribable || {};
                router.push({
                    path: '/subscription-required',
                    query: {
                        type: sub.type || undefined,
                        id: sub.id || undefined,
                        label: sub.label || undefined,
                        return: current.fullPath,
                    },
                });
            }
        }

        // "One device per student" — a mid-session 403 means the device was
        // suspended/reset/reassigned remotely by an admin after this session
        // started. DEVICE_NOT_APPROVED is login-time only (handled locally
        // in login.vue, since /user_login isn't behind auth.webstudent) and
        // never reaches this interceptor.
        if (error?.response?.status === 403 && data?.code === 'DEVICE_SUSPENDED') {
            deviceInfo.notifySuspended();
            const current = router.currentRoute.value;
            if (current.path !== '/device-suspended') {
                router.push('/device-suspended');
            }
        } else if (error?.response?.status === 403 && data?.code === 'DEVICE_BLOCKED') {
            AuthMonitor.stopHeartbeat();
            store.dispatch('logout');
            router.push('/login');
        } else if (data?.code === 'SESSION_REVOKED') {
            // Single Active Session — the token was superseded by a newer login
            // on another device (or an admin Force Logout / Reset Device). This
            // is an AUTHENTICATION event, NOT a network error: never show the
            // offline screen (a 401 carries a response body, so OfflineManager
            // already ignores it). Immediately clear ALL auth state (token, user,
            // localStorage/sessionStorage auth keys, axios header, Vuex store)
            // via the logout action, then send the student to login with a clear
            // message — without waiting for a manual logout. Other open tabs on
            // the old device hit the same 401 on their next request and clear too.
            // Also stop the session-validity heartbeat (Feature: instant kick-out)
            // so it doesn't keep polling a now-logged-out session.
            AuthMonitor.stopHeartbeat();
            store.dispatch('logout');
            const notice = 'تم تسجيل الدخول من جهاز آخر، يرجى تسجيل الدخول مرة أخرى.';
            try {
                // Set AFTER logout() (which wipes auth keys) under a non-auth key
                // so it survives the clear and login.vue can surface it.
                sessionStorage.setItem('session_revoked_notice', notice);
            } catch (e) { /* storage unavailable — query flag still triggers the message */ }
            const current = router.currentRoute.value;
            if (current.path !== '/login') {
                router.push({ path: '/login', query: { session_revoked: 1 } });
            }
        } else if (
            error?.response?.status === 401
            && typeof error?.config?.url === 'string'
            && error.config.url.indexOf('/auth/check-auth') !== -1
        ) {
            // Session-validity monitor (Feature: instant kick-out on revoked
            // session). check-auth returned 401 WITHOUT the SESSION_REVOKED code
            // above (e.g. a garbage/unrecognisable stored token) — still a real
            // "you are no longer authenticated" answer, so run the identical
            // logout flow with the generic Arabic session-expired message. This
            // branch is intentionally scoped to check-auth's own responses only
            // (never a blanket "any 401 logs out"), and — critically — it never
            // issues another check-auth call, so it cannot recurse.
            AuthMonitor.stopHeartbeat();
            store.dispatch('logout');
            try {
                sessionStorage.setItem('session_revoked_notice', 'انتهت جلسة تسجيل الدخول، برجاء تسجيل الدخول مرة أخرى.');
            } catch (e) { /* storage unavailable — query flag still triggers the message */ }
            const current = router.currentRoute.value;
            if (current.path !== '/login') {
                router.push({ path: '/login', query: { session_revoked: 1 } });
            }
        } else if (error?.response?.status === 426 || data?.code === 'APP_UPDATE_REQUIRED') {
            // Mandatory app-update — an outdated mobile app hit a protected API.
            // This is the SERVER-enforced path (can't be bypassed from the
            // frontend): show the blocking update overlay. It's a real HTTP
            // response, so OfflineManager never treats it as a network error.
            AppUpdateManager.triggerFrom426(data);
        }

        return Promise.reject(error);
    }
);

OfflineManager.attachAxios(axios);
OfflineManager.init(router);

// "One device per student" — only attaches X-Device-* headers when running
// inside the Flutter WebView wrapper; a no-op in a plain browser.
deviceInfo.attach(axios);
deviceInfo.init();

// Mandatory app-update — check the minimum required version very early. Runs
// through the deviceInfo interceptor, so the request carries the X-Device-*
// version headers; the backend returns update_required=false for browsers.
AppUpdateManager.check(axios);

// Session-validity monitor (Feature: instant kick-out on revoked session).
// Runs the initial check-auth call (only if the client believes it's
// authenticated) and binds the single global visibilitychange listener. The
// 60s heartbeat is started/stopped centrally by the router, only while the
// current route is inside the authenticated student app.
if (!initialHomepagePreview) AuthMonitor.init();

const app = createApp(App);
app.config.globalProperties.axios = axios;
app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            prefix: 'p',
            darkModeSelector: false,
            cssLayer: false
        }
    }
});
app.use(router);
app.use(store);
app.directive('phone', phoneDirective);
app.mount('#app');
